"use client";

import type { MediaAssetSlim } from "./types";

export async function waitForProcessedMedia(
  key: string,
  options: { signal?: AbortSignal; retry?: boolean } = {},
): Promise<MediaAssetSlim> {
  const started = Date.now();
  const response = await fetch("/api/media/uploads", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ key, retry: options.retry }),
    signal: options.signal,
  });
  if (!response.ok) throw new Error("Unable to finish this upload. Try again.");
  while (true) {
    const result = await fetch(
      `/api/media/uploads?key=${encodeURIComponent(key)}`,
      { cache: "no-store", signal: options.signal },
    );
    if (!result.ok) throw new Error("Unable to check this upload. Try again.");
    const status = (await result.json()) as {
      state: string;
      error: string | null;
      asset: MediaAssetSlim | null;
    };
    if (status.state === "ready" && status.asset) return status.asset;
    if (status.state === "failed")
      throw new Error(status.error || "Media processing failed. Try again.");
    if (Date.now() - started > 30 * 60_000)
      throw new Error(
        "Media is still processing. You can retry without uploading again.",
      );
    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        clearTimeout(timer);
        reject(options.signal?.reason ?? new Error("Upload cancelled"));
      };
      const timer = window.setTimeout(() => {
        options.signal?.removeEventListener("abort", abort);
        resolve();
      }, 1500);
      if (options.signal?.aborted) abort();
      else options.signal?.addEventListener("abort", abort, { once: true });
    });
  }
}
